import { BookmarkRegular } from '@fluentui/react-icons';
import { Course as CourseTeamsServicesType } from '@mnspro-cloud/teams-services';
import cn from 'classnames';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CommunicationMessage,
  Illustrations,
  IllustrationSize,
} from '../../Communication/CommunicationMessage';
import { CourseTileData } from '../../CourseTile/CourseTile.interface';
import { Text } from '../../Text/Text';
import { WidgetsHeadline } from '../WidgetsHeadline/WidgetsHeadline';
import { MyCoursesWidgetCourseList } from './MyCoursesWidgetCourseList';
import { MyCoursesWidgetSkeleton } from './MyCoursesWidgetSkeleton';

export interface MyCoursesWidgetProps {
  isLoading: boolean;
  courses: CourseTileData[] | CourseTeamsServicesType[];
  /**
   * Primary click handler. When omitted, the course tiles are inert. Teams
   * omits this and passes {@link onOpenInBrowser} instead, so clicking a tile
   * never springboards the user out of Teams.
   */
  onClick?: (courseId: string) => void;
  /**
   * When set, each tile renders a ⋯-menu with an "open in browser" action.
   * Teams passes an `app.openLink`-based handler here.
   */
  onOpenInBrowser?: (courseId: string) => void;
  error?: Error | null;
  classNames?: string;
  useDarkIllustration?: boolean;
}

export const MyCoursesWidget: FC<MyCoursesWidgetProps> = ({
  isLoading,
  courses,
  onClick,
  onOpenInBrowser,
  error,
  classNames,
  useDarkIllustration = false,
}) => {
  const { t } = useTranslation();

  return (
    <div className={cn('coursesWidget', classNames)}>
      <WidgetsHeadline
        content={t('dashboard:course:myCourses', { defaultValue: 'Meine Kurse' })}
        icon={<BookmarkRegular fontSize={24} />}
      />
      {isLoading && <MyCoursesWidgetSkeleton />}
      {!isLoading && !error && courses.length > 0 && (
        <MyCoursesWidgetCourseList
          courses={courses}
          onClick={onClick}
          onOpenInBrowser={onOpenInBrowser}
        />
      )}
      {!isLoading && !error && courses.length === 0 && (
        <CommunicationMessage
          illustration={Illustrations.Empty}
          headline=""
          text={t('dashboard:course:emptyMessageText', { defaultValue: 'Für den ausgewählten Zeitraum, sind Ihnen keine Kurse zugeordnet' })}
          illustrationSize={IllustrationSize.Small}
          useDarkIllustration={useDarkIllustration}
        />
      )}
      {!isLoading && error && (
        <Text color="danger">
          {t('dashboard:course:errorMessageText', { defaultValue: 'Fehler beim Laden der Kurse' })}
        </Text>
      )}
    </div>
  );
};
